import { useEffect, useState } from 'react';
import type { TenantCardMeta } from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { fetchTenantById } from '../lib/repositories/tenants.js';
import { CardRegistrationForm } from '../components/CardRegistrationForm.js';

/**
 * TP-20: テナント自身がお支払い用クレジットカードを登録/差替するページ（tenant-portal）。
 * - 対象は **自テナント**（`useAuth().tenantId`）。tenant-admin が登録、staff は閲覧のみ。
 * - 各テナントの支払い状況の横断管理は saas-admin 側（TP-25）で別途行う。
 * - S-90: 認可ガード（loading / signed-out / saas-admin 転送）とヘッダーは Layout（AppShell）
 *   に集約したため、本ページはコンテンツのみを描画する。
 */
export function BillingPage() {
  const { role, tenantId } = useAuth();
  const [card, setCard] = useState<TenantCardMeta | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const t = await fetchTenantById(tenantId);
        if (!cancelled) setCard(t?.payjpDefaultCard ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // tenant-admin（自テナント）/ 兼任 saas-admin はカード登録可。staff は閲覧のみ。
  const canManage = !!tenantId && (role === 'tenant-admin' || role === 'saas-admin');

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-h-page font-semibold text-ink">お支払い設定</h1>
      <p className="mt-1 text-body text-ink-2">
        月額利用料の決済に使うクレジットカードを登録します（3Dセキュア対応 /
        カード番号は当社で保持しません）。
      </p>

      {!tenantId ? (
        <p className="mt-6 rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink">
          テナント管理者アカウントでログインしてください。
        </p>
      ) : (
        <section className="mt-6 rounded-xl bg-white px-[30px] py-[26px]">
          <h2 className="text-h-card font-semibold text-ink">現在の登録カード</h2>
          {loading ? (
            <p className="mt-2 text-body text-ink-3">読み込み中…</p>
          ) : card ? (
            <p className="mt-2 text-body text-ink">
              {card.brand} •••• {card.last4}（有効期限{' '}
              <span className="ui-num tabular-nums">
                {String(card.expMonth).padStart(2, '0')}/{card.expYear}
              </span>
              ）
            </p>
          ) : (
            <p className="mt-2 text-body text-ink-3">未登録</p>
          )}

          <h2 className="mt-8 text-h-card font-semibold text-ink">
            {card ? 'カードを差し替える' : 'カードを登録する'}
          </h2>
          {canManage ? (
            <div className="mt-4">
              <CardRegistrationForm
                tenantId={tenantId}
                onRegistered={(c) => setCard(c)}
              />
            </div>
          ) : (
            <p className="mt-2 rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink">
              カードの登録・変更はテナント管理者のみ可能です。
            </p>
          )}
        </section>
      )}
    </div>
  );
}
